/**
 * Lot 10 — Bench domaine (moyenne + % ≥ seuil).
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { analyticsApi, ApiRequestError, type DomainBenchDto } from "../../api/api";
import {
  AnalyticsFilterBar,
  type AnalyticsFiltersState,
} from "../../components/ui/AnalyticsFilterBar";
import { PageHeader } from "../../components/ui/PageHeader";

export function DomainBenchPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<AnalyticsFiltersState>({
    year: new Date().getFullYear(),
  });
  const [data, setData] = useState<DomainBenchDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void analyticsApi
      .domainBench(filters)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) {
          setData(null);
          setError(err instanceof ApiRequestError ? err.message : t("analytics_load_failed"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters, t]);

  const chart = (data?.domains ?? []).map((d) => ({
    domain: d.domain,
    avg: d.avgScore,
    pct: d.pctAtOrAbove,
  }));

  return (
    <div className="sm-page">
      <PageHeader title={t("domain_bench")} subtitle={t("domain_bench_sub")} />
      <AnalyticsFilterBar filters={filters} onChange={setFilters} />
      {error ? <p className="sm-error">{error}</p> : null}
      {loading ? <p className="sm-muted">{t("loading")}</p> : null}

      {!loading && data ? (
        <>
          <p className="sm-muted">
            {t("target_threshold")}: <strong>{data.target}</strong>
          </p>
          <div className="sm-card sm-card-pad" style={{ marginBottom: 16 }}>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="domain" />
                <YAxis yAxisId="avg" domain={[0, 4]} />
                <YAxis yAxisId="pct" orientation="right" domain={[0, 100]} />
                <Tooltip />
                <Legend />
                <Bar yAxisId="avg" dataKey="avg" name={t("avg_score")} fill="#1F4E79" />
                <Bar yAxisId="pct" dataKey="pct" name={t("pct_at_threshold")} fill="#5B8C5A" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="sm-card">
            <div className="sm-table-wrap">
              <table className="sm-table">
                <thead>
                  <tr>
                    <th>{t("domain")}</th>
                    <th>{t("experts")}</th>
                    <th>{t("assessments")}</th>
                    <th>{t("avg_score")}</th>
                    <th>{t("pct_at_threshold")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.domains.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="sm-muted" style={{ textAlign: "center" }}>
                        {t("no_data")}
                      </td>
                    </tr>
                  ) : (
                    data.domains.map((d) => (
                      <tr key={d.domain}>
                        <td>{d.domain}</td>
                        <td style={{ textAlign: "center" }}>{d.expertCount}</td>
                        <td style={{ textAlign: "center" }}>{d.assessmentCount}</td>
                        <td style={{ textAlign: "center" }}>{d.avgScore}</td>
                        <td style={{ textAlign: "center" }}>{d.pctAtOrAbove}%</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
